import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '@/components/ui/Icon';
import type { RockDTO } from '@/api/endpoints';
import { sound } from '@/game/audio/sound';
import { useRun } from '@/store/run';
import { useSession } from '@/store/session';

/**
 * The rock, full screen — where the actual mining happens.
 *
 * Multi-stage crack visuals, impact sparks, floating damage numbers, a combo
 * counter, and iOS WebKit touch-conflict prevention with hold-to-mine.
 *
 * **This screen is the cave, not a system dialog over it.** It was first built in
 * a borrowed glassmorphism language — `bg-slate-950`, `bg-white/5`, `border-white/15`,
 * a navy `rgba(10,15,26,.92)` backdrop — which is a cold grey vocabulary dropped
 * into a warm torchlit game. It is also the screen a player looks at for most of a
 * run, so it was the loudest thing in the app disagreeing with everything else.
 * Everything here now draws from the same tokens as the corridor: `--surface`,
 * `--hairline`, the torch ramp. See `docs/ART_DIRECTION.md` §1.
 */

/**
 * How large the rock draws, as a share of the smaller viewport edge.
 *
 * At 60 it left roughly a third of a 390×780 phone as empty black above and below
 * the only thing on screen you can interact with. This is a lens on one rock; the
 * rock should fill it.
 */
const ROCK_VMIN = 86;

/**
 * The torch, applied to a studio render — same reasoning as `RockField`, same
 * direction of light. A rock that is warm and shaded in the corridor and neutral
 * grey the instant you tap it is two different rocks.
 */
const ROCK_LIT =
  'brightness(.82) contrast(1.26) saturate(1.05) sepia(.34) hue-rotate(-12deg)';

/**
 * Is this a touch device?
 *
 * Only used to decide which of two true sentences to print. The game ships to
 * phones, so it defaults to the touch wording if the query cannot be answered —
 * being told about a key you do not have is worse than not being told about one
 * you do.
 */
function useCoarsePointer(): boolean {
  const [coarse, setCoarse] = useState(true);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(pointer: coarse)');
    setCoarse(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setCoarse(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return coarse;
}

interface FloatingHit {
  id: number;
  text: string;
  x: number;
  y: number;
  isCrit: boolean;
}

export function RockFocus({ rock, onClose, onSwung }: {
  rock: RockDTO;
  onClose: () => void;
  /** Fired per swing so the corridor underneath keeps its own feedback wired. */
  onSwung?: () => void;
}) {
  const lampEquipment = useSession((s) => s.equipment['lamp']);
  const axeEquipment = useSession((s) => s.equipment['axe']);
  const lampLevel = lampEquipment?.level ?? 1;
  const axeLevel = axeEquipment?.level ?? 1;
  const axeName = axeEquipment?.name ?? `Axe Lvl ${axeLevel}`;
  const axeDamage = axeEquipment?.stats.damage;
  const axeImgNum = axeLevel < 10 ? `0${axeLevel}` : `${axeLevel}`;
  const axePath = `/axes/axe_${axeImgNum}.png`;
  const { swing, loot, phase, crackStage, lastCrit, throttled } = useRun();
  const coarse = useCoarsePointer();
  const [isStriking, setIsStriking] = useState(false);
  const [comboCount, setComboCount] = useState(0);
  const [floatingHits, setFloatingHits] = useState<FloatingHit[]>([]);
  const holdInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const comboTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nextHitId = useRef(0);

  /* Close when the rock is gone or loot card has taken over */
  useEffect(() => {
    if (loot || phase === 'loot' || phase === 'walking' || phase === 'idle') onClose();
  }, [loot, phase, onClose]);

  /* Core swing function with haptics, combo tracking, and floating numbers */
  const handleSwing = useCallback(() => {
    setIsStriking(true);
    setTimeout(() => setIsStriking(false), 180);

    sound.playSfx(lastCrit ? 'crit' : 'hit');

    // WebKit iOS Haptic Vibrations
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      if (lastCrit) {
        navigator.vibrate([18, 12, 18]);
      } else {
        navigator.vibrate(10);
      }
    }

    // Increment combo counter & reset combo decay timer
    setComboCount((c) => c + 1);
    if (comboTimer.current) clearTimeout(comboTimer.current);
    comboTimer.current = setTimeout(() => setComboCount(0), 1200);

    // Spawn floating damage text
    const hitId = ++nextHitId.current;
    const isCrit = Boolean(lastCrit);
    const text = isCrit ? 'CRIT!' : 'HIT!';
    const offsetX = (Math.random() - 0.5) * 60;
    const offsetY = (Math.random() - 0.5) * 40;

    setFloatingHits((hits) => [
      ...hits.slice(-4), // Keep max 5 floating hit popups
      { id: hitId, text, x: offsetX, y: offsetY, isCrit },
    ]);

    setTimeout(() => {
      setFloatingHits((hits) => hits.filter((h) => h.id !== hitId));
    }, 750);

    onSwung?.();
    void swing();
  }, [lastCrit, onSwung, swing]);

  /* Press and hold continuous mining loop for iOS Web & Touchscreens */
  const startHoldSwing = useCallback(() => {
    handleSwing();
    if (holdInterval.current) clearInterval(holdInterval.current);
    holdInterval.current = setInterval(handleSwing, 210);
  }, [handleSwing]);

  const stopHoldSwing = useCallback(() => {
    if (holdInterval.current) {
      clearInterval(holdInterval.current);
      holdInterval.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (holdInterval.current) clearInterval(holdInterval.current);
      if (comboTimer.current) clearTimeout(comboTimer.current);
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault(); handleSwing();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleSwing, onClose]);

  const btnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const btn = btnRef.current;
    if (!btn) return;
    const preventTouch = (e: TouchEvent) => {
      if (e.cancelable) e.preventDefault();
    };
    btn.addEventListener('touchstart', preventTouch, { passive: false });
    return () => {
      btn.removeEventListener('touchstart', preventTouch);
    };
  }, []);

  const stage = Math.max(0, Math.min(4, crackStage));
  const material = rock.material?.key ?? 'loose';
  const total = rock.required_hits ?? 0;
  const done = Math.min(rock.hits_registered, total || rock.hits_registered);
  const isTreasure = rock.visual_type === 'treasure_rock';

  return (
    <div
      className="absolute inset-0 z-30 flex flex-col gap-3 p-3 transition-all duration-300 select-none"
      style={{
        /* A pool of torchlight on the rock face, falling off to cave dark. The
           material's own colour tints the pool, so quartz and basalt do not light
           the same — but it lands on the cave ramp, not on a navy one.

           Two layers, and the order matters: the tint is a *separate* gradient
           over an opaque base. Folding the material colour into the first stop of
           one gradient makes the whole inner region partly transparent, because
           the alpha interpolates outward from that stop — the corridor's HUD text
           and the miner were legible straight through the middle of this screen,
           which reads as a rendering fault rather than as depth. */
        backgroundImage:
          `radial-gradient(78% 52% at 50% 46%, ${rock.material?.color_a ?? '#6E5240'}30 0%,` +
          ' rgba(0,0,0,0) 72%),' +
          ' radial-gradient(115% 82% at 50% 46%, var(--cave-wall) 0%,' +
          ' var(--cave-bg) 46%, var(--cave-void) 100%)',
        backgroundColor: 'var(--cave-void)',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none',
        touchAction: 'none',
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Mining an unidentified rock"
      onContextMenu={(e) => e.preventDefault()}
    >
      <style>{`
        @keyframes rfChip {
          from { transform: scale(.55); opacity: 1 }
          to   { transform: scale(1.25); opacity: 0 }
        }
        /* A player who has asked for less motion still needs to know the swing
           landed — so the chips stop flying and simply flash instead. */
        @media (prefers-reduced-motion: reduce) {
          .animate-\\[rfChip_\\.22s_ease-out\\] { animation: none; opacity: .9 }
        }
      `}</style>

      {/* ── What you are hitting, and with what ── */}
      <div className="flex shrink-0 items-center justify-between gap-3 rounded-[var(--r-md)]
                      border border-[var(--hairline)] bg-[var(--surface)] p-3 backdrop-blur-md
                      shadow-[var(--shadow-lift)] mt-[calc(env(safe-area-inset-top)+2px)]">
        <div className="min-w-0">
          {/*
            No name, no tier, no "Trash Rock" label. What is inside a rock is the surprise the
            loop is built on, and printing the answer above it spends that before the first
            swing — the loot card is where it belongs, and it still names everything there.

            The GLOW stays. `docs/GAME_DESIGN.md` §116 makes the golden hairline cracks a
            deliberate tell, the lamp's `treasure_glow` upgrade exists to strengthen it, and
            the `reveal_treasure` ad sells exactly this information. Hiding it would strand
            two systems and make an upgrade path pay for nothing. So the rock is *seen* and
            not *told*: a player who has earned the lamp reads the cracks, everyone else
            swings and finds out.
          */}
          <div className="flex items-center gap-2">
            <h2 className="font-pixel text-[11px] tracking-wider text-torch-core uppercase drop-shadow-[0_0_8px_rgba(255,180,60,0.5)]">
              Unidentified
            </h2>
          </div>
          <p className="mt-1 text-[10px] text-ink-dim flex flex-wrap items-center gap-1.5">
            <span className="flex items-center gap-1">
              <Icon name="rock" size={12} className="text-orange-400" />
              <span>Break it to find out</span>
            </span>
            {/* The two numbers that decide whether this rock can be broken at all,
                and how fast. Not shop copy — the axe's *name* is a shop fact and is
                left to the screen reader; in here what matters is the damage. */}
            <span className="flex items-center gap-1 rounded border border-[var(--hairline)]
                             bg-black/25 px-1.5 py-0.5 font-pixel text-[6.5px] text-[var(--torch-warm)]">
              <Icon name="axe" size={10} />
              <span>LV {axeLevel}{axeDamage ? ` · DMG ${axeDamage}` : ''}</span>
              <span className="sr-only">{axeName}</span>
            </span>
            <span className="flex items-center gap-1 rounded border border-[var(--hairline)]
                             bg-black/25 px-1.5 py-0.5 font-pixel text-[6.5px] text-[var(--torch-core)]">
              <Icon name="lamp" size={10} />
              <span>LV {lampLevel}</span>
              <span className="sr-only">Lamp level</span>
            </span>
            {rock.blessed && <span className="text-[var(--ok)] font-semibold">· Blessed</span>}
            {rock.spirit?.active && <span className="text-kyat-glow font-semibold">· Spirit Active</span>}
          </p>
        </div>

        {/* Combo Counter Badge */}
        {comboCount > 1 && (
          <div className="flex shrink-0 items-center gap-1 rounded-[var(--r-sm)] border
                          border-[var(--hairline-hi)] bg-[var(--ui-panel)] px-2.5 py-1
                          font-pixel text-[9px] text-[var(--torch-core)] shadow-md">
            <Icon name="flame" size={12} className="text-[var(--torch-warm)]" />
            <span className="tabular">x{comboCount}</span>
            <span className="sr-only">combo</span>
          </div>
        )}

        <button
          type="button"
          onClick={onClose}
          aria-label="Back to the corridor"
          className="grid h-10 w-10 shrink-0 place-items-center rounded-[var(--r-sm)]
                     border border-[var(--hairline-hi)] bg-[var(--ui-panel)]
                     text-[var(--ink)] transition-all active:scale-95"
          style={{ touchAction: 'manipulation' }}
        >
          <Icon name="close" size={16} />
        </button>
      </div>

      {/* ── Mining Arena & Tap Target ── */}
      <button
        ref={btnRef}
        type="button"
        onPointerDown={startHoldSwing}
        onPointerUp={stopHoldSwing}
        onPointerLeave={stopHoldSwing}
        onPointerCancel={stopHoldSwing}
        onContextMenu={(e) => e.preventDefault()}
        aria-label="Swing at the unidentified rock"
        className="relative flex flex-1 items-center justify-center border-0 bg-transparent p-0 outline-none select-none cursor-pointer"
        style={{ touchAction: 'none', WebkitTapHighlightColor: 'transparent', WebkitUserSelect: 'none', WebkitTouchCallout: 'none' }}
      >
        {/* Ambient Rock Lighting Ring */}
        <div
          className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-40"
          style={{
            background: `radial-gradient(circle at 50% 50%, ${rock.material?.color_a ?? '#F59E0B'}44 0%, transparent 60%)`,
          }}
        />

        {/* Dynamic Multi-Stage Rock Container.
            Driven by `isStriking`, which is set on every swing, rather than by
            `impact`, which only ticks when the crack stage advances. On a rock
            needing more hits than it has crack stages most taps produced no recoil
            at all, so the swing that did land looked identical to one that did not
            — the single most common complaint about tap games. Toggling the class
            also restarts the animation without remounting, which the old `key`
            did by tearing down and rebuilding the <img> five times a second. */}
        <span
          className={`relative block max-h-full max-w-full
                      ${isStriking ? 'animate-[rockHit_.19s_ease-out]' : ''}`}
          style={{ width: `${ROCK_VMIN}vmin`, aspectRatio: '1' }}
        >
          <img
            src={`/rocks/rock_${material}_${stage}.webp`}
            alt=""
            className="h-full w-full select-none object-contain"
            style={{
              /* Lit like the corridor, and shadowed away from the light so it has
                 weight rather than hovering in the middle of a gradient. */
              filter: `${ROCK_LIT} drop-shadow(10px 16px 20px rgba(0,0,0,.75))`,
            }}
            draggable={false}
            fetchPriority="high"
          />

          {/* Fissure Crack Textures & Glowing Vein Overlays (Stages 1-4) */}
          {stage > 0 && (
            <svg
              className="pointer-events-none absolute inset-0 h-full w-full select-none"
              viewBox="0 0 100 100"
              aria-hidden
            >
              {/* Stage 1 Fissure */}
              <path
                d="M 32,28 L 48,52 L 42,75"
                stroke={isTreasure ? '#FDE047' : '#FB923C'}
                strokeWidth={1.5}
                strokeLinecap="round"
                fill="none"
                opacity={0.85}
                className="drop-shadow-[0_0_6px_rgba(251,146,60,0.8)]"
              />
              {/* Stage 2 Branching Cracks */}
              {stage >= 2 && (
                <path
                  d="M 48,52 L 68,44 M 42,75 L 25,82"
                  stroke={isTreasure ? '#FEF08A' : '#F97316'}
                  strokeWidth={1.8}
                  strokeLinecap="round"
                  fill="none"
                  opacity={0.9}
                  className="drop-shadow-[0_0_8px_rgba(249,115,22,0.9)]"
                />
              )}
              {/* Stage 3 & 4 Glowing Magma / Crystal Shatter Fractures */}
              {stage >= 3 && (
                <g className="animate-pulse">
                  <path
                    d="M 28,42 L 52,38 L 74,68 M 48,52 L 62,80"
                    stroke={isTreasure ? '#FFFFFF' : '#FF4500'}
                    strokeWidth={2.2}
                    strokeLinecap="round"
                    fill="none"
                    opacity={0.95}
                    className="drop-shadow-[0_0_12px_rgba(255,69,0,1)]"
                  />
                </g>
              )}
            </svg>
          )}

          {/* Treasure Glow Overlay */}
          {isTreasure && (
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 rounded-full"
              style={{
                background: `radial-gradient(46% 40% at 50% 52%, rgba(255,214,140,${
                  0.15 + 0.12 * stage
                }) 0%, transparent 70%)`,
                mixBlendMode: 'screen',
              }}
            />
          )}

          {/* Impact: chips thrown off the strike, drawn rather than typed.
              This was four emoji pinned to the corners of a square — ✨💥💥✨ —
              which is neither where a pick lands nor the same picture on two
              phones, since each OS ships its own drawing of them. Eight shards
              on a circle, thrown outward, in the torch ramp. */}
          {isStriking && (
            <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <span className="h-[22%] w-[22%] rounded-full bg-[var(--torch-warm)]/40 animate-ping" />
              <svg
                className="absolute h-[62%] w-[62%] animate-[rfChip_.22s_ease-out]"
                viewBox="-50 -50 100 100"
                aria-hidden
              >
                {Array.from({ length: 8 }, (_, i) => {
                  const a = (i / 8) * Math.PI * 2;
                  const r0 = 16 + (i % 3) * 4;
                  const r1 = r0 + 13 + (i % 2) * 6;
                  return (
                    <line
                      key={i}
                      x1={Math.cos(a) * r0}
                      y1={Math.sin(a) * r0}
                      x2={Math.cos(a) * r1}
                      y2={Math.sin(a) * r1}
                      stroke={i % 2 ? 'var(--torch-core)' : 'var(--torch-warm)'}
                      strokeWidth={2.4}
                      strokeLinecap="round"
                    />
                  );
                })}
              </svg>
            </span>
          )}
        </span>

        {/* Floating Damage Numbers */}
        {floatingHits.map((h) => (
          <span
            key={h.id}
            className={`pointer-events-none absolute font-pixel tracking-wider ${
              h.isCrit
                ? 'text-[15px] text-[var(--torch-core)] drop-shadow-[0_0_12px_rgba(255,179,71,1)]'
                : 'text-[12px] text-[var(--torch-warm)] drop-shadow-[0_0_8px_rgba(245,197,66,.8)]'
            } animate-[floatUp_.75s_ease-out_forwards]`}
            style={{
              transform: `translate(${h.x}px, ${h.y}px)`,
            }}
          >
            {h.text}
          </span>
        ))}

        {/* Dynamic Axe Swing Arc Overlay */}
        <div
          className={`pointer-events-none absolute right-[15%] top-[20%] w-32 h-32 transition-all duration-150 origin-bottom-right ${
            isStriking
              ? 'opacity-100 rotate-[-45deg] scale-110 translate-x-[-20px] translate-y-[20px]'
              : 'opacity-0 rotate-[35deg] scale-90 translate-x-[20px] translate-y-[-20px]'
          }`}
        >
          <img
            src={axePath}
            alt=""
            className="w-full h-full object-contain filter drop-shadow-[0_0_12px_rgba(255,200,80,0.6)]"
          />
        </div>

        {/* Critical Strike Notification.
            Gold-white rather than the old hot pink: #FF5FA2 is `--r-mythic-a`, a
            *rarity* colour, and spending it on a crit told the player "mythic"
            several times a second on a common rock. A crit is the torch flaring,
            so it is the top of the torch ramp. */}
        {lastCrit && (
          <span className="pointer-events-none absolute left-1/2 top-[14%] flex -translate-x-1/2
                           items-center gap-1.5 font-pixel text-[12px] tracking-widest
                           text-[var(--torch-core)]
                           drop-shadow-[0_0_12px_rgba(255,179,71,.95)] animate-bounce">
            <Icon name="flame" size={14} className="text-[var(--torch-warm)]" />
            CRITICAL
          </span>
        )}
      </button>

      {/* ── How close it is to breaking, and how to keep going ── */}
      <div className="shrink-0 rounded-[var(--r-md)] border border-[var(--hairline)]
                      bg-[var(--surface)] p-3.5 backdrop-blur-md shadow-[var(--shadow-lift)]
                      mb-[calc(env(safe-area-inset-bottom)+6px)]">
        {total > 0 && (
          <>
            <div className="mb-2 flex items-center justify-between font-pixel text-[9px] tracking-wider">
              <span className="text-[var(--ink-dim)]">DURABILITY</span>
              <span className="tabular font-bold text-[var(--ink)]">{done} / {total}</span>
            </div>
            {/* Segment per hit, not a continuous bar. Rocks here take three to a
                dozen hits, and on a smooth bar one swing out of nine is a sliver
                you cannot see move — which reads as a swing that did not land.
                Ticks make every single hit a visible event. */}
            <div className="flex h-3 gap-[3px]" role="img"
                 aria-label={`${done} of ${total} hits landed`}>
              {Array.from({ length: total }, (_, i) => (
                <span
                  key={i}
                  className="h-full flex-1 rounded-[2px] border border-[var(--hairline)]
                             transition-all duration-200"
                  style={
                    i < done
                      ? {
                          background: 'linear-gradient(180deg,var(--torch-core),var(--torch-warm))',
                          boxShadow: '0 0 8px rgba(255,179,71,.55)',
                        }
                      : { background: 'rgba(0,0,0,.45)' }
                  }
                />
              ))}
            </div>
          </>
        )}

        <div className="mt-3 flex items-center justify-center gap-2 text-center font-pixel
                        text-[8.5px] leading-[1.5] tracking-wider">
          <Icon name="axe" size={12} className="shrink-0 text-[var(--torch-warm)]" />
          <span className={throttled ? 'animate-pulse text-[var(--warn)]' : 'text-[var(--ink-dim)]'}>
            {throttled
              ? 'SWINGING TOO FAST — EASE OFF'
              /* The old copy read "TAP & HOLD ROCK OR SPACE TO MINE CONTINUOUSLY"
                 on every device. Space is real — the key handler is right there —
                 but on the phone this game ships to it is advice about a key the
                 player does not have, in the one place they need to be told what
                 to do. Say the one that applies. */
              : coarse
                ? 'HOLD THE ROCK TO KEEP SWINGING'
                : 'HOLD THE ROCK, OR PRESS SPACE'}
          </span>
        </div>
      </div>
    </div>
  );
}
